/**
 * One sample-dataset detail page.
 *
 * Layout: a two-column split — metadata on the left (rows × cols,
 * variables, source/attribution, downloads) and a static preview of
 * the canonical Sparrow analysis on the right. Below, an "Open in
 * Sparrow" call-to-action.
 *
 * "Open in Sparrow": Sparrow does NOT currently expose a `?load=<url>`
 * hook (its URL-driven loading paths are PWA file-launch, the share
 * target API, and portal-minted #share tokens — none accept an
 * arbitrary public URL). So this CTA does two things in sequence:
 *
 *   1. Triggers a CSV download in the user's browser.
 *   2. Opens sparrow.dasc.nl in a new tab.
 *
 * The user then drops the just-downloaded file onto Sparrow's file
 * drop zone. Slightly clunky, but honest. When Sparrow grows a
 * "load from URL" handler we can shed step 1.
 */

import { Link, useParams } from "react-router-dom"
import {
  AlertCircle, ArrowLeft, ArrowRight, ArrowUpRight, Download,
  FileSpreadsheet, Inbox, Tag,
} from "lucide-react"
import { findSample, type SampleVariable } from "@/lib/samples"
import { SPARROW_URL } from "@/lib/sparrow"
import { PreviewBlock } from "@/components/PreviewBlock"

export function SampleDetail() {
  const { id } = useParams()
  const sample = findSample(id)

  if (!sample) {
    return (
      <div className="max-w-2xl mx-auto rounded-lg border border-border bg-card p-8">
        <div className="flex items-center gap-3 text-foreground">
          <AlertCircle className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Sample not found</h2>
        </div>
        <p className="mt-3 text-muted-foreground">
          We don't have a sample called <code className="text-foreground">{id}</code>.
        </p>
        <Link
          to="/samples"
          className="mt-6 inline-flex items-center gap-1.5 text-primary font-medium hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to all samples
        </Link>
      </div>
    )
  }

  // Public path that the user's browser will fetch.
  // `import.meta.env.BASE_URL` is "/" by default; honoring it keeps the
  // sample-CSV link correct if the SPA is ever served from a subpath.
  const csvHref = `${import.meta.env.BASE_URL}samples/${sample.id}.csv`
  const downloadName = `${sample.id}.csv`

  function openInSparrow() {
    // Step 1: trigger a download by clicking a synthetic <a download>.
    const a = document.createElement("a")
    a.href = csvHref
    a.download = downloadName
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Step 2: open Sparrow in a new tab so the user can drop the
    // file they just downloaded.
    window.open(SPARROW_URL, "_blank", "noopener,noreferrer")
  }

  return (
    <div>
      <Link
        to="/samples"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        All samples
      </Link>

      <header className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
        <h1
          className="font-display text-4xl tracking-tight text-foreground"
          style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
        >
          {sample.title}
        </h1>
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground border border-border rounded-full px-2 py-0.5">
          <Tag className="h-3 w-3" />
          {sample.category}
        </span>
      </header>
      <p className="text-muted-foreground max-w-3xl mb-8">{sample.blurb}</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: metadata + downloads */}
        <aside className="lg:col-span-5 space-y-4">
          <div className="rounded-lg border border-border bg-card">
            <div className="px-4 py-3 border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              Dataset
            </div>
            <dl className="grid grid-cols-2 gap-y-3 gap-x-6 px-4 py-4 text-sm">
              <Field label="Rows"           value={sample.rows.toLocaleString()} />
              <Field label="Columns"        value={String(sample.cols)} />
              <Field label="File format"    value={sample.fileFormat.toUpperCase()} />
              <Field label="Canonical analysis" value={sample.canonical} span />
              <Field label="Source / attribution" value={sample.attribution} span />
            </dl>
          </div>

          <div className="rounded-lg border border-border bg-card">
            <div className="px-4 py-3 border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              Variables ({sample.variables.length})
            </div>
            <ul className="px-4 py-3 text-sm divide-y divide-border/60">
              {sample.variables.map(v => (
                <VariableRow key={v.name} v={v} />
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
              Download
            </div>
            <a
              href={csvHref}
              download={downloadName}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-card text-foreground font-medium text-sm hover:bg-secondary transition-colors"
            >
              <Download className="h-4 w-4" />
              {sample.id}.csv
            </a>
            <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
              CSV ships with this page — opens in Sparrow, Excel, Numbers, R, Python,
              or anything that reads RFC-4180. No .sav build yet — open the CSV and
              re-export from Sparrow if you need .sav.
            </p>
          </div>
        </aside>

        {/* Right: preview + Open-in-Sparrow */}
        <section className="lg:col-span-7 space-y-4">
          <PreviewBlock preview={sample.preview} />

          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center shrink-0">
                  <Inbox className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Open in Sparrow</h3>
                  <p className="text-sm text-muted-foreground max-w-md mt-1 leading-relaxed">
                    Downloads <code className="text-foreground">{downloadName}</code> and opens
                    Sparrow in a new tab. Drop the file into Sparrow's home screen to start the analysis.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={openInSparrow}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity shadow-sm"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Download &amp; open
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
              We pop a new tab and trigger a download because Sparrow doesn't currently
              accept arbitrary URLs as a load source — when that lands upstream, this
              button will hand the file directly.
            </p>
          </div>

          <div className="rounded-lg border border-dashed border-border p-5">
            <h3 className="font-semibold text-foreground mb-1">Want a different sample?</h3>
            <p className="text-sm text-muted-foreground">
              The gallery has {7 - 1} more curated datasets across classification, survey, time
              series, and conversion analyses.
            </p>
            <Link
              to="/samples"
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              Back to gallery <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}

function Field({ label, value, span }: { label: string; value: string; span?: boolean }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-foreground">{value}</dd>
    </div>
  )
}

function VariableRow({ v }: { v: SampleVariable }) {
  return (
    <li className="py-2 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <code className="font-mono text-sm text-foreground">{v.name}</code>
        {v.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{v.description}</p>
        )}
      </div>
      <span className={"shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border " + typeStyle(v.type)}>
        {v.type}
      </span>
    </li>
  )
}

function typeStyle(t: SampleVariable["type"]): string {
  switch (t) {
    case "scale":   return "text-primary border-primary/40 bg-primary/5"
    case "nominal": return "text-accent-foreground border-accent/40 bg-accent/10"
    case "ordinal": return "text-foreground border-border bg-secondary"
    case "date":    return "text-muted-foreground border-border bg-card"
    case "string":  return "text-muted-foreground border-border bg-card"
  }
}
